import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static values = { auditId: Number }
  static targets = [
    "status",
    "analyzedPages",
    "totalPages",
    "progress",
    "progressBar",
    "elapsedTime",
    "healthScore",
    "healthGrade",
    "issuesCount"
  ]

  // Check if we're in development mode
  isDevelopment() {
    return window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1' ||
           window.location.hostname.includes('localhost')
  }

  connect() {
    if (this.isDevelopment()) {
      console.log("AuditProgressController connected for audit", this.auditIdValue)
    }
    
    // Check for race condition FIRST: if page shows running but audit is actually completed
    // This must happen before subscribe() to catch cases where the audit completed before page load
    const healthScoreCard = this.element.querySelector('[data-audit-status]')
    const hasHealthScore = !!this.element.querySelector('[data-audit-progress-target="healthScore"]')
    
    if (healthScoreCard) {
      const auditStatus = healthScoreCard.dataset.auditStatus
      const auditCompleted = healthScoreCard.dataset.auditCompleted === 'true'
      
      if (this.isDevelopment()) {
        console.log("Race condition check:", {
          auditStatus,
          auditCompleted,
          hasHealthScore,
          element: healthScoreCard
        })
      }
      
      // If audit is completed but page doesn't show completed state, reload immediately
      if ((auditStatus === 'completed' || auditCompleted) && !hasHealthScore) {
        if (this.isDevelopment()) {
          console.log("Race condition detected: Audit is completed but page shows running state - reloading...")
        }
        window.location.reload()
        return
      }
    }
    
    this.subscribe()
    
    // Only start elapsed time updater if audit is running (not completed)
    const statusElement = this.element.querySelector('[data-audit-progress-target="status"]')
    const startedAtElement = this.element.querySelector('[data-audit-started-at]')
    const isRunning = statusElement && (
      statusElement.textContent.includes("Progress") || 
      statusElement.textContent.includes("Finalizing") ||
      statusElement.textContent.includes("Pending")
    )
    
    if (isRunning || (startedAtElement && !hasHealthScore)) {
      this.startElapsedTimeUpdater()
      
      // Add periodic status check to handle race conditions
      // If audit is stuck in "finalizing" or "running" for more than 5 seconds, check if it's actually completed
      this.statusCheckInterval = setInterval(() => {
        this.checkForCompletion()
      }, 5000) // Check every 5 seconds
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    if (this.elapsedInterval) {
      clearInterval(this.elapsedInterval)
    }
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval)
    }
  }

  checkForCompletion() {
    // Check if we're showing "Finalizing" or "In Progress" but audit might actually be completed
    const statusElement = this.element.querySelector('[data-audit-progress-target="status"]')
    const isRunning = statusElement && (statusElement.textContent.includes("Finalizing") || statusElement.textContent.includes("Progress"))
    
    if (this.isDevelopment()) {
      console.log("checkForCompletion called:", {
        statusElement: statusElement?.textContent,
        isRunning,
        auditStatus: this.element.querySelector('[data-audit-status]')?.dataset.auditStatus,
        auditCompleted: this.element.querySelector('[data-audit-completed]')?.dataset.auditCompleted
      })
    }
    
    if (isRunning) {
      // Check the data attributes first (fastest check)
      const healthScoreCard = this.element.querySelector('[data-audit-status]')
      if (healthScoreCard) {
        const auditStatus = healthScoreCard.dataset.auditStatus
        const auditCompleted = healthScoreCard.dataset.auditCompleted === 'true'
        
        if (this.isDevelopment()) {
          console.log("Data attributes check:", { auditStatus, auditCompleted })
        }
        
        if (auditStatus === 'completed' || auditCompleted) {
          if (this.isDevelopment()) {
            console.log("Detected completed status via data attributes, reloading page...")
          }
          window.location.reload()
          return
        }
      }
      
      // If data attributes don't show completed, fetch the page to check
      // This handles cases where the page was rendered before completion
      fetch(window.location.href, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
          'Cache-Control': 'no-cache'
        },
        credentials: 'same-origin'
      })
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const newStatus = doc.querySelector('[data-audit-status]')?.dataset.auditStatus
        const newCompleted = doc.querySelector('[data-audit-completed]')?.dataset.auditCompleted === 'true'
        
        if (newStatus === 'completed' || newCompleted) {
          if (this.isDevelopment()) {
            console.log("Detected completed status via fetch, reloading page...")
          }
          window.location.reload()
        }
      })
      .catch(error => {
        console.error("Error checking audit status:", error)
      })
    }
  }


  subscribe() {
    const consumer = createConsumer()
    const subscriptionParams = { channel: "AuditChannel", id: this.auditIdValue }
    
    if (this.isDevelopment()) {
      console.log("Subscribing to AuditChannel with params:", subscriptionParams)
    }
    
    this.subscription = consumer.subscriptions.create(
      subscriptionParams,
      {
        received: (data) => {
          if (this.isDevelopment()) {
            console.log("Received audit update:", data)
          }
          this.updateAudit(data)
        },
        connected: () => {
          if (this.isDevelopment()) {
            console.log("✅ Connected to AuditChannel for audit", this.auditIdValue)
            console.log("Audit ID value:", this.auditIdValue)
            console.log("Element:", this.element)
            console.log("Data attributes:", {
              auditStatus: this.element.querySelector('[data-audit-status]')?.dataset.auditStatus,
              auditCompleted: this.element.querySelector('[data-audit-completed]')?.dataset.auditCompleted
            })
          }
        },
        disconnected: () => {
          if (this.isDevelopment()) {
            console.log("❌ Disconnected from AuditChannel")
          }
        },
        rejected: () => {
          console.error("❌ Rejected from AuditChannel - check permissions")
        }
      }
    )
  }

  updateAudit(data) {
    // Update status
    if (data.status && this.hasStatusTarget) {
      this.statusTarget.textContent = this.capitalize(data.status)
    }

    // Update analyzed pages
    if (data.analyzed_pages !== undefined && this.hasAnalyzedPagesTarget) {
      this.analyzedPagesTarget.textContent = data.analyzed_pages
    }

    // Update total pages
    if (data.total_pages !== undefined && this.hasTotalPagesTarget) {
      this.totalPagesTarget.textContent = data.total_pages
    }

    // Update progress percentage
    if (data.progress_percentage !== undefined) {
      if (this.hasProgressTarget) {
        this.progressTarget.textContent = `${data.progress_percentage}%`
      }
      if (this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = `${data.progress_percentage}%`
      }
    }

    // Update elapsed time (calculated client-side for real-time updates)
    if (data.status === "running") {
      // Start elapsed time updater if not already running
      if (!this.elapsedInterval) {
        this.startElapsedTimeUpdater()
      }
      
      const elapsed = this.getElapsedTime()
      if (elapsed !== null) {
        const elapsedText = elapsed < 60 
          ? `${Math.round(elapsed)}s` 
          : `${Math.round(elapsed / 60)}min`
        
        // Update subtitle
        const subtitle = this.element.querySelector('[data-audit-progress-subtitle]')
        if (subtitle) {
          if (data.finalizing) {
            subtitle.textContent = `Finalizing audit... • ${data.analyzed_pages || 0} / ${data.total_pages} pages analyzed (${data.progress_percentage || 100}%)`
          } else if (data.total_pages > 0) {
            subtitle.textContent = `Running for ${elapsedText} • ${data.analyzed_pages || 0} / ${data.total_pages} pages analyzed (${data.progress_percentage || 0}%)`
          } else {
            subtitle.textContent = `Running for ${elapsedText}`
          }
        }
      }
      
      // Update status text if finalizing
      if (data.finalizing && this.hasStatusTarget) {
        this.statusTarget.textContent = "Finalizing"
      }
      
      // Update progress bar to 100% if finalizing
      if (data.finalizing && this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = "100%"
      }
      
      // Update progress text if finalizing
      const progressText = this.element.querySelector('[data-audit-progress-text]')
      if (data.finalizing && progressText) {
        progressText.textContent = "Finalizing audit..."
      }
    }

    // Update health score and grade when completed
    if (data.status === "completed") {
      // Stop elapsed time updater
      if (this.elapsedInterval) {
        clearInterval(this.elapsedInterval)
        this.elapsedInterval = null
      }

      // Reload the page to show the completed state with all data
      // This ensures all server-rendered content (issues, strengths, etc.) is displayed
      window.location.reload()
    }

    // Update progress display in status text
    if (data.status === "running" && data.analyzed_pages !== undefined && data.total_pages !== undefined) {
      this.updateProgressDisplay(data.analyzed_pages, data.total_pages, data.progress_percentage)
    }
  }

  updateProgressDisplay(analyzedPages, totalPages, progressPercentage) {
    // Find and update the progress text in the subtitle
    const subtitle = this.element.querySelector('[data-audit-progress-subtitle]')
    if (subtitle && totalPages > 0) {
      const elapsed = this.getElapsedTime()
      const elapsedText = elapsed ? `Running for ${elapsed < 60 ? `${Math.round(elapsed)}s` : `${Math.round(elapsed / 60)}min`}` : "In progress"
      subtitle.textContent = `${elapsedText} • ${analyzedPages} / ${totalPages} pages analyzed (${progressPercentage || 0}%)`
    }

    // Update progress bar if it exists
    const progressBar = this.element.querySelector('[data-audit-progress-bar]')
    if (progressBar && progressPercentage !== undefined) {
      progressBar.style.width = `${progressPercentage}%`
    }

    // Update progress text if it exists
    const progressText = this.element.querySelector('[data-audit-progress-text]')
    if (progressText && progressPercentage !== undefined) {
      progressText.textContent = `${progressPercentage}% complete`
    }
  }

  getElapsedTime() {
    // Try to get elapsed time from the started_at timestamp
    const startedAtElement = this.element.querySelector('[data-audit-started-at]')
    if (startedAtElement && startedAtElement.dataset.timestamp) {
      try {
        const started = new Date(startedAtElement.dataset.timestamp)
        const now = new Date()
        return (now - started) / 1000
      } catch (e) {
        console.error("Error calculating elapsed time:", e)
      }
    }
    return null
  }

  startElapsedTimeUpdater() {
    // Update elapsed time every second when audit is running
    if (this.elapsedInterval) {
      clearInterval(this.elapsedInterval)
    }
    
    this.elapsedInterval = setInterval(() => {
      const elapsed = this.getElapsedTime()
      if (elapsed !== null) {
        const elapsedText = elapsed < 60 
          ? `${Math.round(elapsed)}s` 
          : `${Math.round(elapsed / 60)}min`
        
        // Update subtitle if it exists
        const subtitle = this.element.querySelector('[data-audit-progress-subtitle]')
        if (subtitle) {
          const currentText = subtitle.textContent
          // Only update if it's showing running status
          if (currentText.includes("Running for") || currentText.includes("In progress")) {
            const analyzedPages = this.element.querySelector('[data-audit-progress-target="analyzedPages"]')?.textContent || "0"
            const totalPages = this.element.querySelector('[data-audit-progress-target="totalPages"]')?.textContent || "0"
            const progress = this.element.querySelector('[data-audit-progress-target="progress"]')?.textContent || "0"
            if (totalPages !== "0") {
              subtitle.textContent = `Running for ${elapsedText} • ${analyzedPages} / ${totalPages} pages analyzed (${progress}%)`
            } else {
              subtitle.textContent = `Running for ${elapsedText}`
            }
          }
        }
      }
    }, 1000)
  }

  getGradeClass(grade) {
    const baseClass = "mt-2 text-2xl font-semibold"
    switch (grade) {
      case "A":
        return `${baseClass} text-green-400`
      case "B":
        return `${baseClass} text-blue-400`
      case "C":
        return `${baseClass} text-yellow-400`
      case "D":
        return `${baseClass} text-orange-400`
      default:
        return `${baseClass} text-red-400`
    }
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }
}

